import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { serviceImages } from '../../constants/images';
import Footer from '../Footer';
const SoftwareTesting = () => {
  useEffect(() => {
    document.title = 'Software Testing - Global Excellence';
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: '🔍',
      title: 'Manual Testing',
      description: 'Comprehensive manual testing for quality assurance'
    },
    {
      icon: '🤖',
      title: 'Automated Testing',
      description: 'Efficient automated testing solutions'
    },
    {
      icon: '📱',
      title: 'Mobile Testing',
      description: 'Testing across various mobile platforms'
    },
    {
      icon: '🌐',
      title: 'Web Testing',
      description: 'Thorough web application testing'
    },
    {
      icon: '🔒',
      title: 'Security Testing',
      description: 'Identifying and fixing security vulnerabilities'
    },
    {
      icon: '📊',
      title: 'Performance Testing',
      description: 'Ensuring optimal application performance'
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Test Planning',
      description: 'Define testing strategy and scope'
    },
    {
      number: '02',
      title: 'Test Design',
      description: 'Create detailed test cases and scenarios'
    },
    {
      number: '03',
      title: 'Test Execution',
      description: 'Perform testing according to plan'
    },
    {
      number: '04',
      title: 'Defect Reporting',
      description: 'Document and report identified issues'
    },
    {
      number: '05',
      title: 'Regression Testing',
      description: 'Verify fixes and check for new issues'
    },
    {
      number: '06',
      title: 'Test Closure',
      description: 'Finalize and document test results'
    }
  ];

  const technologies = [
    { icon: '🔧', name: 'Selenium' },
    { icon: '🔄', name: 'JUnit' },
    { icon: '📱', name: 'Appium' },
    { icon: '🌐', name: 'Postman' },
    { icon: '🔍', name: 'JMeter' },
    { icon: '📊', name: 'TestNG' },
    { icon: '🔒', name: 'OWASP ZAP' },
    { icon: '🤖', name: 'Cypress' }
  ];

  return (
    <>
    <div className="service-component-wrapper">
      {/* Hero Section */}
      <section className="service-component-hero">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="service-hero-content"
        >
          <h1 className="service-hero-title">Software Testing</h1>
          <p className="service-hero-subtitle">
            Ensuring quality and reliability through comprehensive testing solutions
          </p>
        </motion.div>
      </section>

      {/* Overview Section */}
      <section className="service-overview">
        <div className="service-overview-content">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="service-overview-text"
          >
            <h2>Comprehensive Testing Services</h2>
            <p>
              Our software testing services ensure your applications meet the highest
              quality standards. We combine manual and automated testing approaches
              to deliver reliable, bug-free software solutions that exceed user
              expectations.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="service-overview-image"
          >
            <img src={serviceImages.softwareTesting} alt="Software Testing" />
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="service-features">
        <h2>Our Testing Services</h2>
        <div className="features-grid">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="feature-card"
            >
              <span className="feature-icon">{feature.icon}</span>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="service-process">
        <h2>Our Testing Process</h2>
        <div className="process-grid">
          {processSteps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="process-card"
            >
              <span className="process-number">{step.number}</span>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Technologies Section */}
      <section className="service-technologies">
        <h2>Testing Tools & Technologies</h2>
        <div className="tech-grid">
          {technologies.map((tech, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="tech-item"
            >
              <span className="tech-icon">{tech.icon}</span>
              <span className="tech-name">{tech.name}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-cta">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="cta-content"
        >
          <h2 style={{color: 'white'}}>Ready to Ensure Your Software Quality?</h2>
          <p style={{color: 'white'}}>Let's discuss how we can help you achieve flawless software performance</p>
          <div className="cta-buttons">
            <Link to="/contactus" className="cta-button primary">
              Get Started
            </Link>
            <Link to="/services" className="cta-button secondary">
              View All Services
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
    <Footer />
    </>
  );
};    

export default SoftwareTesting; 